import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileCheck, Lock, Scan, CheckCircle2, XCircle, AlertTriangle, ArrowRight, User, History } from 'lucide-react';
import { motion } from 'framer-motion';
import TrustStrip from '@/components/TrustStrip';
import { useApp } from '@/context/AppContext';
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics';

const LandingPage = () => {
  const navigate = useNavigate();
  const { clearQueue, user, loginWithGoogle, logout, isAuthenticated } = useApp();
  const { trackEvent } = useAnalytics();

  // Clear queue when user visits landing page (new session)
  useEffect(() => {
    clearQueue();
    // Track landing page view
    trackEvent(ANALYTICS_EVENTS.LANDING_VIEW);
  }, [clearQueue, trackEvent]);

  const problems = [
    { icon: XCircle, text: 'Missing apostilles', color: 'text-destructive' },
    { icon: AlertTriangle, text: 'Name inconsistencies', color: 'text-warning' },
    { icon: XCircle, text: 'Incorrect formatting', color: 'text-destructive' },
    { icon: AlertTriangle, text: 'Invalid translations', color: 'text-warning' },
  ];

  const steps = [
    { num: '1', title: 'Upload your document', icon: FileCheck },
    { num: '2', title: 'We remove personal data instantly', icon: Lock },
    { num: '3', title: 'Get a Readiness Score + Fix Plan', icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="font-public text-sm text-slate-600 hover:text-trust transition-colors">How it works</a>
            <a href="#pricing" className="font-public text-sm text-slate-600 hover:text-trust transition-colors">Pricing</a>
            <a href="#privacy" className="font-public text-sm text-slate-600 hover:text-trust transition-colors">Privacy</a>
            {isAuthenticated && (
              <button
                onClick={() => navigate('/history')}
                className="font-public text-sm text-slate-600 hover:text-trust transition-colors inline-flex items-center gap-1"
                data-testid="nav-history"
              >
                <History className="w-4 h-4" />
                My History
              </button>
            )}
          </nav>
          {/* Auth buttons */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-sm text-slate-700 hover:text-trust transition-colors"
                  data-testid="user-dashboard-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                  <span className="hidden sm:inline font-public">{user?.name?.split(' ')[0]}</span>
                </button>
              </div>
            ) : (
              <button
                onClick={loginWithGoogle}
                className="text-sm font-public text-slate-600 hover:text-trust transition-colors inline-flex items-center gap-2"
                data-testid="login-btn"
              >
                <User className="w-4 h-4" />
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-16 md:pt-32 md:pb-24">
        <div className="absolute inset-0 bg-gradient-to-br from-trust/5 via-transparent to-verify/5" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Route Badge */}
            <div className="inline-flex items-center gap-2 bg-trust/10 border border-trust/20 rounded-full px-4 py-2 mb-6">
              <span className="text-lg">🇺🇸 → 🇪🇸</span>
              <span className="font-public text-sm text-trust font-medium">USA to Spain</span>
            </div>
            
            <h1 className="font-outfit font-bold text-5xl md:text-7xl tracking-tighter leading-none text-slate-900 mb-6" data-testid="hero-headline">
              Check Your Spain Visa Documents —{' '}
              <span className="text-trust">Without Sharing Your Identity</span>
            </h1>
            <p className="font-public text-lg md:text-xl text-slate-600 mb-4 leading-relaxed">
              Moving from the US to Spain? Find document issues that could delay your visa in 60 seconds. Your documents are never stored.
            </p>
            <p className="font-public text-sm text-slate-500 mb-8">
              More countries coming soon — we're starting with USA → Spain
            </p>
            <button
              onClick={() => navigate('/select-country')}
              className="bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
              data-testid="hero-cta-button"
            >
              Check My Documents
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Trust Strip */}
      <TrustStrip />

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-outfit font-bold text-3xl md:text-5xl text-slate-900 mb-4">
              The #1 reason for visa rejection? Preventable mistakes.
            </h2>
            <p className="font-public text-lg text-slate-600">They get rejected for:</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {problems.map((problem, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200"
              >
                <problem.icon className={`w-6 h-6 ${problem.color}`} />
                <span className="font-public font-medium text-slate-900">{problem.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-outfit font-bold text-3xl md:text-5xl text-slate-900 mb-4">
              Upload → We redact → We analyze
            </h2>
          </div>

          {/* How it works */}
          <div id="how-it-works" className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="text-center"
              >
                <div className="bg-trust/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-trust" />
                </div>
                <div className="font-mono text-xs uppercase tracking-wider text-trust mb-2">Step {step.num}</div>
                <h3 className="font-public font-semibold text-lg text-slate-900">{step.title}</h3>
              </motion.div>
            ))}
          </div>

          {/* Example output */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-2xl mx-auto bg-white border-l-4 border-l-trust rounded-lg shadow-xl p-8"
          >
            <h3 className="font-outfit font-bold text-2xl text-slate-900 mb-6">Example Output</h3>
            <div className="space-y-4">
              <div className="flex items-baseline gap-3">
                <span className="text-5xl font-outfit font-bold text-warning">78</span>
                <span className="text-xl text-slate-400">/100</span>
                <span className="font-public text-slate-600">Readiness Score</span>
              </div>
              <div className="space-y-2 pt-4 border-t border-slate-200">
                <div className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                  <span className="font-public text-slate-900"><strong>Critical:</strong> Missing apostille</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                  <span className="font-public text-slate-900"><strong>Warning:</strong> Name inconsistency detected</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-900"><strong>Passed:</strong> Format correct</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-outfit font-bold text-3xl md:text-5xl text-slate-900 mb-4">Simple Pricing</h2>
            <p className="font-public text-lg text-slate-600">Pay per document or save with bundles</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Single Document */}
            <div 
              onClick={() => navigate('/select-country')}
              className="bg-white border border-slate-200 rounded-lg p-8 cursor-pointer hover:border-trust hover:shadow-lg transition-all active:scale-[0.98]"
              data-testid="pricing-single"
            >
              <h3 className="font-outfit font-bold text-2xl text-slate-900 mb-2">Single Document</h3>
              <div className="mb-4">
                <span className="text-5xl font-outfit font-bold text-trust">$10</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">1 document analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">Privacy-safe analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">Instant results</span>
                </li>
              </ul>
              <div className="text-center">
                <span className="text-trust font-public font-semibold text-sm">Click to get started →</span>
              </div>
            </div>

            {/* Bundle 5 docs */}
            <div 
              onClick={() => navigate('/select-country')}
              className="bg-white border border-slate-200 rounded-lg p-8 cursor-pointer hover:border-trust hover:shadow-lg transition-all active:scale-[0.98]"
              data-testid="pricing-bundle-5"
            >
              <h3 className="font-outfit font-bold text-2xl text-slate-900 mb-2">5 Documents</h3>
              <div className="mb-4">
                <span className="text-5xl font-outfit font-bold text-trust">$40</span>
                <span className="text-lg text-slate-400 line-through ml-2">$50</span>
              </div>
              <div className="bg-verify/10 text-verify px-2 py-1 rounded text-xs font-mono mb-4 inline-block">
                SAVE 20%
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">5 document analyses</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">Full readiness scoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public text-slate-600">Premium insights</span>
                </li>
              </ul>
              <div className="text-center">
                <span className="text-trust font-public font-semibold text-sm">Click to get started →</span>
              </div>
            </div>

            {/* Bundle Full Review */}
            <div 
              onClick={() => navigate('/select-country')}
              className="bg-trust text-trust-foreground rounded-lg p-8 shadow-xl relative cursor-pointer hover:bg-trust/90 hover:shadow-2xl transition-all active:scale-[0.98]"
              data-testid="pricing-bundle-10"
            >
              <div className="absolute -top-3 right-8 bg-verify text-verify-foreground px-3 py-1 rounded-md text-xs font-mono uppercase tracking-wider">Best Value</div>
              <h3 className="font-outfit font-bold text-2xl mb-2">10+ Documents</h3>
              <div className="mb-4">
                <span className="text-5xl font-outfit font-bold">$70+</span>
                <span className="text-lg opacity-60 line-through ml-2">$100+</span>
              </div>
              <div className="bg-verify text-verify-foreground px-2 py-1 rounded text-xs font-mono mb-4 inline-block">
                SAVE 30%
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public">Complete visa bundle</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public">Maximum savings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-verify mt-0.5" />
                  <span className="font-public">Priority support</span>
                </li>
              </ul>
              <div className="text-center">
                <span className="font-public font-semibold text-sm">Click to get started →</span>
              </div>
            </div>
          </div>
          <p className="text-center text-slate-500 text-sm mt-8">
            Example: 13 Student Visa documents = $91 (instead of $130) - Save $39!
          </p>
        </div>
      </section>

      {/* Privacy Section */}
      <section id="privacy" className="py-16 md:py-24 bg-verify/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="bg-verify/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-verify" />
            </div>
            <h2 className="font-outfit font-bold text-3xl md:text-5xl text-slate-900 mb-4">Your Privacy is Our Priority</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <Lock className="w-6 h-6 text-verify mb-3" />
              <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">Temporary Processing</h3>
              <p className="font-public text-slate-600">Documents are processed temporarily only</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <Scan className="w-6 h-6 text-verify mb-3" />
              <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">Automatic Redaction</h3>
              <p className="font-public text-slate-600">Sensitive information is automatically removed</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <FileCheck className="w-6 h-6 text-verify mb-3" />
              <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">Immediate Deletion</h3>
              <p className="font-public text-slate-600">Files are deleted immediately after analysis</p>
            </div>
            <div className="bg-white p-6 rounded-lg border border-slate-200">
              <Shield className="w-6 h-6 text-verify mb-3" />
              <h3 className="font-public font-semibold text-lg text-slate-900 mb-2">No Data Selling</h3>
              <p className="font-public text-slate-600">We never store or sell your data</p>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-slate-100 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center font-public text-sm text-slate-600" data-testid="landing-disclaimer">
            <strong>Important Disclaimer:</strong> This tool identifies common document issues and potential risks. 
            It does not verify identity, provide legal advice, certify documents, or guarantee visa approval. 
            Results are for informational purposes only and should not replace professional immigration consultation.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-trust text-trust-foreground py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5" />
            <span className="font-outfit font-bold text-lg">Visa-Ready Document Checker</span>
          </div>
          <p className="font-public text-sm opacity-80">Privacy-first document checking for US → Spain visa applicants</p>
          <p className="font-public text-xs opacity-60 mt-1">More migration routes coming soon</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
